import MapboxDraw, { DrawCustomMode, DrawCustomModeThis, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import { Position } from "geojson";
import { isEqual } from "lodash";
import mapboxgl from "mapbox-gl";
import { Utils } from "mapbox-gl-draw-snap-mode";

import { ValidationState } from "fond/draw/validation/helper";
import { enumerate } from "fond/utils";

import simpleSelect from "../../base/simpleSelect";

import { getFeatureId, pullInFeatures, refreshSnappedStatus } from "./helper";
import { onAfterDragAerial } from "./onAfterDragAerial";
import { onTrashAerial } from "./onTrashAerial";

const { snap, createSnapList } = Utils;

const { modes } = MapboxDraw;

interface DrawCustomModeState extends ValidationState {
  poleDragInit: {
    connectedSpans: DrawLineString[];
    connectedIds: Set<string | number>;
  } | null;
  snapList: mapboxgl.MapboxGeoJSONFeature[];
  vertices: mapboxgl.MapboxGeoJSONFeature[];
  boxSelecting: boolean;
}

interface DrawCustomModeExtension<S> extends DrawCustomMode<S> {
  dragMove(this: DrawCustomModeThis, state: S, event: MapboxDraw.MapMouseEvent): void;
}

/**
 * This is a custom mode for selecting and dragging poles in the aerial planner.
 * It extends the simple_select mode and adds functionality for snapping to spans.
 */
const spanPoleSelect: DrawCustomModeExtension<DrawCustomModeState> = {
  ...simpleSelect,

  onSetup: function (opts) {
    const state = modes.simple_select.onSetup?.call(this, opts);
    state.options = this._ctx.options;
    state.map = this.map;

    return state;
  },

  /**
   * @param state - The current state of the mode.
   * @param e - The mouse event.
   * @description
   * Monitors the mouse down event & if the user has clicked on a pole we create a list
   * of features the pole can snap to during the onDrag event
   *
   * Note: We only allow poles to be selected & dragged, spans (and their vertices) cannot
   * be dragged.
   */
  onMouseDown: function (state, e) {
    if (!e.featureTarget || e.featureTarget.geometry.type === "LineString") {
      // We don't want to be able to move spans without moving poles.
      simpleSelect.onMouseDown?.call(this, state, e);
    } else {
      const featureId = getFeatureId(e.featureTarget);

      if (e.featureTarget != null && featureId) {
        const pole = this.getFeature(featureId) as DrawPoint;
        const connectedIds = new Set<string | number>();
        const { spans } = pullInFeatures.call(this, pole, connectedIds, e.type);

        refreshSnappedStatus.call(this, pole);

        for (let span of spans) {
          const spanId = getFeatureId(span);
          if (spanId) connectedIds.add(spanId);
        }

        // We need to filter out the current poles connected spans as we don't want to allow snapping to them
        const snapGetFeatures = (_: mapboxgl.Map, draw: MapboxDraw) =>
          draw.getAll().features.filter((feature) => !connectedIds.has(getFeatureId(feature)));
        const [snapList, vertices] = createSnapList(this.map, this._ctx.api, pole, snapGetFeatures);

        // Mutate the local state with the available snapping information
        state.snapList = snapList;
        state.vertices = vertices;
        state.poleDragInit = {
          connectedSpans: spans,
          connectedIds: connectedIds,
        };
      }

      simpleSelect.onMouseDown?.call(this, state, e);
    }
  },

  /**
   * On mouse up we hand over to the shared aerial topology repair, which merges poles
   * dropped on poles and splits spans a pole was dropped onto.
   */
  onMouseUp: function (state, e) {
    const selected = this.getSelected();
    if (selected.length === 1 && !state.boxSelecting && selected[0].type === "Point") {
      onAfterDragAerial.call(this, selected[0], undefined, state.poleDragInit?.connectedIds ?? new Set());

      this.map.fire("draw.update", { features: selected });
    }

    return simpleSelect.onMouseUp?.call(this, state, e);
  },

  /**
   * @param state - The current state of the mode.
   * @param e - The mouse event.
   * @description
   * Handles the drag move event for the span pole select mode.
   * It updates the coordinates of the selected pole and moves any connected spans accordingly.
   */
  dragMove: function (state, e) {
    // Dragging when drag move is enabled
    e.originalEvent.stopPropagation();
    const selected = this.getSelected() as DrawFeature[];

    // We do not allow multiple items to be moved or paths
    if (selected.length > 1 || selected?.[0].type === "LineString") return;

    // If the user is dragging a pole with connected spans we need to update the coordinates of the spans
    // to match the new coordinates of the pole.
    const moveLines = (oldCoords: Position, newCoords: Position) => {
      if (state.poleDragInit?.connectedSpans) {
        for (let f of state.poleDragInit.connectedSpans) {
          const featureId = getFeatureId(f);
          if (featureId) {
            const feature = this.getFeature(featureId) as DrawLineString;
            for (let [i, c] of enumerate(feature.coordinates)) {
              if (isEqual(c, oldCoords)) {
                feature.incomingCoords(feature.coordinates.toSpliced(i, 1, newCoords));
                break;
              }
            }
          }
        }
      }
    };

    // Get the new coordinates of the pole and update the coordinates of the spans
    // connected to the pole.
    const oldCoords = selected[0].getCoordinates() as Position;
    const { lng, lat } = snap(state, e);
    selected[0].updateCoordinate("", lng, lat);

    const newCoords = selected[0].getCoordinates() as Position;
    moveLines(oldCoords, newCoords);
  },

  /**
   * @description
   * This function is called when the user clicks the trash button.
   * It deletes the selected features and any connected spans.
   * It also refreshes the snapped status of any connected poles.
   */
  onTrash: function (state) {
    onTrashAerial.call(this, this.getSelected(), state);
  },
};

export default spanPoleSelect;
